import { useState, useRef, useEffect, useMemo, useCallback } from "react";

/* =================================================================
 * Kreux Matrix — CORE I · Voice Demo (Simulated Playback)
 * Scoped under #matrix-operations-layer .voice-demo
 * Architecture: pre-recorded audio + JSON subtitles via fetch()
 * NO ElevenLabs live API.
 * ================================================================= */

const ASSETS = {
  en: {
    restaurant: [{
      label: "Grand Oak — Restaurant",
      audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_English_restaurant_booking.mp3",
      json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_English_restaurant_booking.json",
    }],
    tourism: [
      { label: "Scenario 1 — Tour Demo",
        audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_English_Tour_Demo.mp3",
        json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_English_Tour_Demo.json" },
    ],
    healthcare: [{
      label: "Doctor Appointment",
      audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_English_Doctor_Appointment.mp3",
      json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_English_Doctor_Appointment.json",
    }],
  },
  de: {
    restaurant: [{
      label: "Restaurant Demo",
      audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Demo_Restorant.mp3?updatedAt=1781801983459",
      json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Demo_Restorant.json",
    }],
    tourism: [
      { label: "Szenario 1 — Tour-Beratung",
        audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Tour_Discussion.mp3",
        json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Tour_Discussion.json" },
      { label: "Szenario 2 — Tourismus · Lösung",
        audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Tourisam_Error_Solution_.mp3",
        json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Tourisam_Error_Solution.json" },
    ],
    healthcare: [{
      label: "Zahnarzt-Termin",
      audio: "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Dentist_Appointment_.mp3",
      json:  "https://ik.imagekit.io/5io9hzxfy/ElevenLabs_German_Dentist_Appointment.json",
    }],
  },
};

const COPY = {
  en: {
    title: "Aria — Digital Call Assistant",
    sub: "Pre-recorded playback · synced subtitles",
    chooseDomain: "Choose a domain",
    restaurant: "Restaurant & Catering",
    tourism: "Tourism & Hospitality",
    healthcare: "Healthcare & Medical",
    pickScenario: "Select a scenario",
    call: "Call the Assistant",
    connecting: "Establishing secure voice link…",
    listening: "Listening",
    speaking: "Speaking",
    assistant: "Aria",
    caller: "Caller",
    cont: "Continue",
    quit: "End Call",
  },
  de: {
    title: "Aria — Digitale Anrufassistentin",
    sub: "Vorab aufgezeichnete Wiedergabe · synchronisierte Untertitel",
    chooseDomain: "Wählen Sie eine Branche",
    restaurant: "Restaurant & Catering",
    tourism: "Tourismus & Gastgewerbe",
    healthcare: "Gesundheit & Medizin",
    pickScenario: "Szenario auswählen",
    call: "Assistent anrufen",
    connecting: "Sichere Sprachverbindung wird aufgebaut…",
    listening: "Hört zu",
    speaking: "Spricht",
    assistant: "Aria",
    caller: "Anrufer",
    cont: "Weiter",
    quit: "Anruf beenden",
  },
};

/* Simulated connection chime via Web Audio API — minimalist sci-fi hum */
function playChime(ctx) {
  if (!ctx) return;
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.18, now + 0.25);
  master.gain.exponentialRampToValueAtTime(0.12, now + 1.6);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 2.4);
  master.connect(ctx.destination);

  const tones = [220, 330, 660, 880];
  tones.forEach((freq, i) => {
    const o = ctx.createOscillator();
    o.type = i < 2 ? "sine" : "triangle";
    o.frequency.setValueAtTime(freq, now + i * 0.08);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.35 / (i + 1), now + 0.35 + i * 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 2.3);
    o.connect(g).connect(master);
    o.start(now + i * 0.08);
    o.stop(now + 2.5);
  });
}

const VoiceDemo = ({ lang = "en" }) => {
  const t = COPY[lang] || COPY.en;
  const langAssets = ASSETS[lang] || ASSETS.en;

  // Selection state
  const [category, setCategory] = useState(null); // 'restaurant'|'tourism'|'healthcare'
  const [scenarioIdx, setScenarioIdx] = useState(null); // index for tourism

  // Call state
  const [phase, setPhase] = useState("idle"); // 'idle'|'connecting'|'live'
  const [segments, setSegments] = useState([]);
  const [activeSegIdx, setActiveSegIdx] = useState(-1);
  const [activeWordIdx, setActiveWordIdx] = useState(-1); // pre-baked karaoke pointer
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [volume, setVolume] = useState(0); // pre-baked from word timestamps (no DSP)

  // Refs
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null); // chime only — no analyser graph
  const rafRef = useRef(0);

  // Reset scenario when category changes
  useEffect(() => { setScenarioIdx(null); }, [category]);

  // Reset selections when language changes (assets differ)
  useEffect(() => {
    setCategory(null);
    setScenarioIdx(null);
  }, [lang]);

  const selection = useMemo(() => {
    if (!category) return null;
    const list = langAssets[category];
    if (!list || list.length === 0) return null;
    if (category === "tourism" && list.length > 1) {
      if (scenarioIdx == null) return null;
      return list[scenarioIdx];
    }
    return list[0];
  }, [category, scenarioIdx, langAssets]);

  // Multiple tourism scenarios exist only when there are >=2 in the language
  const tourismHasMultiple = (langAssets.tourism || []).length > 1;

  /* ────────────── Call lifecycle ────────────── */
  const stopAll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    const a = audioRef.current;
    if (a) {
      try { a.pause(); } catch (_) { /* noop */ }
      a.removeAttribute("src");
      try { a.load(); } catch (_) { /* noop */ }
    }
    setActiveSegIdx(-1);
    setActiveWordIdx(-1);
    setActiveSpeaker(null);
    setVolume(0);
  }, []);

  const endCall = useCallback(() => {
    stopAll();
    setPhase("idle");
    setSegments([]);
  }, [stopAll]);

  const continueCall = useCallback(() => {
    // Graceful exit — same as quit per spec ("close overlay, return to page")
    endCall();
  }, [endCall]);

  // Lock body scroll while overlay is active
  useEffect(() => {
    if (phase === "live" || phase === "connecting") {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [phase]);

  // ESC to quit
  useEffect(() => {
    if (phase === "idle") return;
    const onKey = (e) => { if (e.key === "Escape") endCall(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, endCall]);

  const startCall = useCallback(async () => {
    if (!selection) return;
    setPhase("connecting");
    // Pre-fetch JSON in parallel with chime
    const jsonPromise = fetch(selection.json).then((r) => r.json()).catch(() => null);

    // Init audio context + chime
    let ctx = audioCtxRef.current;
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      audioCtxRef.current = ctx;
    }
    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch (_) { /* noop */ }
    }
    playChime(ctx);

    // Wait for chime + JSON
    const [data] = await Promise.all([
      jsonPromise,
      new Promise((r) => setTimeout(r, 2400)),
    ]);

    if (!data || !data.segments) {
      // graceful fallback
      endCall();
      return;
    }
    setSegments(data.segments);
    setPhase("live");

    // Audio element setup — played natively, NOT routed through AudioContext.
    // Visualizer + karaoke are pre-baked from JSON word timestamps (zero DSP latency).
    const a = audioRef.current;
    if (!a) return;
    a.src = selection.audio;
    a.load();

    try { await a.play(); } catch (err) {
      // autoplay block — show end after
      endCall();
      return;
    }

    // Pre-baked tick: derive segment/word indices + visualizer pulse directly from JSON
    const segs = data.segments;
    let lastSegIdx = -1;
    let lastWordIdx = -1;

    const tick = () => {
      const audio = audioRef.current;
      if (!audio || audio.paused) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const ct = audio.currentTime;

      // Active segment (linear scan — segments are ordered)
      let segIdx = -1;
      for (let i = 0; i < segs.length; i++) {
        if (ct >= segs[i].start_time && ct <= segs[i].end_time) { segIdx = i; break; }
        if (ct < segs[i].start_time) break;
      }
      if (segIdx !== lastSegIdx) {
        lastSegIdx = segIdx;
        setActiveSegIdx(segIdx);
        const sp = segIdx >= 0 ? (segs[segIdx].speaker?.id || null) : null;
        setActiveSpeaker((prev) => (prev === sp ? prev : sp));
      }

      // Active word inside that segment + pre-baked, per-speaker pulse envelope
      let wordIdx = -1;
      let pulse = 0;
      const segSpeaker = segIdx >= 0 ? (segs[segIdx].speaker?.id || null) : null;
      if (segIdx >= 0) {
        const words = segs[segIdx].words || [];
        for (let i = 0; i < words.length; i++) {
          const w = words[i];
          // Include a tiny look-ahead/behind window so the pulse rises/falls cleanly.
          if (ct >= w.start_time - 0.04 && ct <= w.end_time + 0.04) {
            const dur = Math.max(0.06, w.end_time - w.start_time);
            const u = Math.max(0, Math.min(1, (ct - w.start_time) / dur));
            let env;
            if (segSpeaker === "speaker_1") {
              // HUMAN CALLER — sharp attack, fast decay (asymmetric, ~12% peak).
              // Feels reactive and percussive, like real syllables.
              env = u < 0.12
                ? Math.pow(u / 0.12, 0.42)
                : Math.pow(Math.max(0, 1 - (u - 0.12) / 0.88), 1.75);
            } else {
              // ARIA (AI / speaker_0) — smooth fat-bell modulated by a triple-LFO carrier:
              //   • LFO1 = breath        (~0.286 Hz, ω = 1.8 rad/s) — slow primary swell
              //   • LFO2 = heartbeat     (~0.4 Hz,   ω ≈ 2.513 rad/s, ~60° phase offset)
              //   • LFO3 = think-tremor (~3 Hz,    ω ≈ 18.85 rad/s, irrational phase)
              // LFO3 is whisper-faint (weight 0.022) — adds a microscopic "processing"
              // shimmer without overpowering the breath/beat envelope or the syllable sync.
              const bell      = Math.pow(Math.sin(u * Math.PI), 0.55);
              const lfoBreath = Math.sin(ct * 1.8);
              const lfoBeat   = Math.sin(ct * 2.513 + 1.05);
              const lfoThink  = Math.sin(ct * 18.85 + 0.7853);
              // Range: ~0.66 → 1.00 (negligibly wider than the dual-LFO version).
              const carrier   = 0.84 + 0.10 * lfoBreath + 0.06 * lfoBeat + 0.022 * lfoThink;
              env = bell * carrier;
            }
            if (env > pulse) pulse = env;
            // Karaoke pointer = the word whose body currently contains ct.
            if (ct >= w.start_time && ct <= w.end_time && w.text && w.text.trim()) {
              wordIdx = i;
            }
          }
        }
      }
      if (wordIdx !== lastWordIdx) {
        lastWordIdx = wordIdx;
        setActiveWordIdx(wordIdx);
      }
      // Per-speaker smoothing — Caller stays snappy, Aria stays silky.
      const aPrev = 0.25;  // caller smoothing (more raw pulse passes through)
      const aPrevAi = 0.70; // aria smoothing (more carryover, slower glide)
      if (segSpeaker === "speaker_1") {
        setVolume((prev) => prev * aPrev + pulse * (1 - aPrev));
      } else {
        setVolume((prev) => prev * aPrevAi + pulse * (1 - aPrevAi));
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    a.addEventListener("ended", endCall, { once: true });
  }, [selection, endCall]);

  // Active subtitle line — render word-by-word for karaoke highlight
  const activeSegment = activeSegIdx >= 0 ? segments[activeSegIdx] : null;
  const activeWords = activeSegment?.words || null;
  const activeText = activeSegment ? activeSegment.text || "" : "";
  const activeSpeakerLabel = activeSpeaker === "speaker_0" ? t.assistant : (activeSpeaker === "speaker_1" ? t.caller : "");

  // Ring scale derived from volume — per-speaker amplitude bias (Caller punches, Aria glides)
  const ringScaleA = 1 + (activeSpeaker === "speaker_0" ? volume * 0.42 : volume * 0.05); // Aria — tighter swell
  const ringScaleB = 1 + (activeSpeaker === "speaker_1" ? volume * 0.68 : volume * 0.10); // Caller — sharper kick
  const ringDimA = activeSpeaker === "speaker_0" ? 1 : (activeSpeaker ? 0.32 : 0.6);
  const ringDimB = activeSpeaker === "speaker_1" ? 1 : (activeSpeaker ? 0.32 : 0.6);

  const canCall = !!selection && phase === "idle";

  return (
    <div className="voice-demo" data-testid="voice-demo">
      <header className="voice-demo__head">
        <span className="voice-demo__avatar">
          <span className="voice-demo__avatarPulse" />
          <span className="voice-demo__avatarLetter">A</span>
        </span>
        <div className="voice-demo__id">
          <span className="voice-demo__name">{t.title}</span>
          <span className="voice-demo__status">
            <span className="voice-demo__livedot" /> {t.sub}
          </span>
        </div>
      </header>

      {/* ───── Category dropdown ───── */}
      <div className="voice-demo__panel">
        <div className="voice-demo__row">
          <label className="voice-demo__label">{t.chooseDomain}</label>
          <div className="voice-demo__choices" role="radiogroup">
            <button
              type="button"
              role="radio"
              aria-checked={category === "restaurant"}
              className={"voice-demo__chip" + (category === "restaurant" ? " is-on" : "")}
              onClick={() => setCategory("restaurant")}
              data-testid="voice-cat-restaurant"
            >
              <span className="voice-demo__chipDot" /> {t.restaurant}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={category === "tourism"}
              className={"voice-demo__chip" + (category === "tourism" ? " is-on" : "")}
              onClick={() => setCategory("tourism")}
              data-testid="voice-cat-tourism"
            >
              <span className="voice-demo__chipDot" /> {t.tourism}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={category === "healthcare"}
              className={"voice-demo__chip" + (category === "healthcare" ? " is-on" : "")}
              onClick={() => setCategory("healthcare")}
              data-testid="voice-cat-healthcare"
            >
              <span className="voice-demo__chipDot" /> {t.healthcare}
            </button>
          </div>
        </div>

        {/* Tourism scenario sub-choice (only for languages with >1 tourism scenario) */}
        {category === "tourism" && tourismHasMultiple && (
          <div className="voice-demo__row voice-demo__row--sub">
            <label className="voice-demo__label">{t.pickScenario}</label>
            <div className="voice-demo__choices">
              {(langAssets.tourism || []).map((s, i) => (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={scenarioIdx === i}
                  className={"voice-demo__chip voice-demo__chip--sub" + (scenarioIdx === i ? " is-on" : "")}
                  onClick={() => setScenarioIdx(i)}
                  data-testid={`voice-scenario-${i + 1}`}
                >
                  <span className="voice-demo__chipDot" /> {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="voice-demo__cta-row">
          <button
            type="button"
            className={"voice-demo__call" + (canCall ? "" : " is-disabled")}
            onClick={startCall}
            disabled={!canCall}
            data-testid="voice-call-btn"
          >
            <span className="voice-demo__callRing" aria-hidden="true" />
            <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
              <path d="M3 4 L3 8 C3 13 7 17 12 17 L16 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <circle cx="16" cy="17" r="1.4" fill="currentColor" />
            </svg>
            <span>{t.call}</span>
          </button>
        </div>
      </div>

      {/* hidden audio element */}
      <audio ref={audioRef} preload="none" />

      {/* ───── Glassmorphism Call Overlay ───── */}
      {(phase === "connecting" || phase === "live") && (
        <div className={"voice-overlay" + (phase === "live" ? " is-live" : " is-connecting")} role="dialog" aria-modal="true" data-testid="voice-overlay">
          <div className="voice-overlay__scrim" />
          <div className="voice-overlay__panel">
            <div className="voice-overlay__corners" aria-hidden="true">
              <span /><span /><span /><span />
            </div>

            <header className="voice-overlay__head">
              <span className="voice-overlay__tag">
                <span className={"voice-overlay__livedot" + (phase === "live" ? " is-live" : "")} />
                {phase === "connecting" ? t.connecting : "LIVE · SECURE LINE"}
              </span>
            </header>

            {/* Visualizers */}
            <div className="voice-overlay__stage">
              {/* Caller (left) */}
              <div
                className={"voice-vis voice-vis--caller" + (activeSpeaker === "speaker_1" ? " is-active" : "")}
                style={{ "--scale": ringScaleB, "--dim": ringDimB }}
              >
                <span className="voice-vis__ring voice-vis__ring--a" />
                <span className="voice-vis__ring voice-vis__ring--b" />
                <span className="voice-vis__ring voice-vis__ring--c" />
                <span className="voice-vis__core">
                  <span className="voice-vis__coreInner">C</span>
                </span>
                <span className="voice-vis__label">{t.caller}</span>
              </div>

              {/* Center subtitle band */}
              <div className="voice-overlay__center">
                <div className="voice-overlay__subWrap">
                  <span className="voice-overlay__speakerTag" data-active={!!activeSpeakerLabel}>
                    {activeSpeakerLabel || (phase === "connecting" ? "" : "—")}
                  </span>
                  <p className="voice-overlay__sub" key={activeSegIdx}>
                    {activeWords && activeWords.length > 0
                      ? activeWords.map((w, i) => {
                          let cls = "voice-overlay__word";
                          if (i < activeWordIdx) cls += " is-spoken";
                          else if (i === activeWordIdx) cls += " is-active";
                          return (
                            <span key={i} className={cls}>{w.text}</span>
                          );
                        })
                      : activeText}
                  </p>
                </div>
              </div>

              {/* Assistant (right) */}
              <div
                className={"voice-vis voice-vis--assistant" + (activeSpeaker === "speaker_0" ? " is-active" : "")}
                style={{ "--scale": ringScaleA, "--dim": ringDimA }}
              >
                <span className="voice-vis__ring voice-vis__ring--a" />
                <span className="voice-vis__ring voice-vis__ring--b" />
                <span className="voice-vis__ring voice-vis__ring--c" />
                <span className="voice-vis__core voice-vis__core--ai">
                  <span className="voice-vis__coreInner">A</span>
                </span>
                <span className="voice-vis__label">{t.assistant}</span>
              </div>
            </div>

            <footer className="voice-overlay__foot">
              <button type="button" className="voice-overlay__btn voice-overlay__btn--ghost" onClick={continueCall} data-testid="voice-continue">
                <span>{t.cont}</span>
              </button>
              <button type="button" className="voice-overlay__btn voice-overlay__btn--primary" onClick={endCall} data-testid="voice-quit">
                <span>{t.quit}</span>
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceDemo;
